import { useState } from 'react';
import styled from 'styled-components';
import {
  useDeleteChatSessionMutation,
  useListChatSessionsQuery,
  useStartChatMutation,
} from '@shared/api';
import { ChatPanel } from '@widgets/ChatPanel/ChatPanel';
import { Button } from '@shared/ui/Button';
import { Card, CardSubtitle } from '@shared/ui/Card';
import { Loader } from '@shared/ui/Progress';
import type { ChatSession } from '@entities/chat/types';

const Layout = styled.div`
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: ${({ theme }) => theme.spacing(5)};
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const SidebarColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const NewBtn = styled(Button)`
  width: 100%;
`;

// SessionItem был styled.button, но внутри лежит ещё одна <button> для удаления,
// что валиден HTML запрещает (validateDOMNesting). Меняем на div с role=button
// + keyboard support — семантика та же, вложенность чистая.
const SessionItem = styled.div<{ $active: boolean }>`
  text-align: left;
  background: ${({ $active, theme }) =>
    $active ? theme.colors.primarySoft : theme.colors.backgroundElevated};
  border: 1px solid
    ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.border)};
  color: ${({ theme }) => theme.colors.text.primary};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(2.5)} ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
  font-family: inherit;
  cursor: pointer;
  user-select: none;
  outline: none;
  transition:
    background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    border-color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderStrong};
  }
  &:focus-visible {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primarySoft};
  }
`;

const SessionTitle = styled.div`
  font-weight: 500;
  margin-bottom: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SessionMeta = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.muted};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Empty = styled(Card)`
  text-align: center;
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: 14px;
`;

function fmtDate(s: string): string {
  const d = new Date(s);
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function ChatTab() {
  const [activeId, setActiveId] = useState<string | null>(null);
  const { data, isLoading } = useListChatSessionsQuery({
    scope: 'knowledge',
  });
  const [startChat, { isLoading: starting }] = useStartChatMutation();
  const [deleteSession] = useDeleteChatSessionMutation();

  // Никаких ручных refetch — useStartChatMutation и useDeleteChatSessionMutation
  // invalidates 'ChatSession', список перезагрузится автоматически.
  async function newSession() {
    const res = await startChat({ scope: 'knowledge' }).unwrap();
    setActiveId(res.id);
  }

  async function remove(id: string) {
    await deleteSession(id).unwrap();
    if (activeId === id) setActiveId(null);
  }

  const sessions: ChatSession[] = data?.items ?? [];

  return (
    <Layout>
      <SidebarColumn>
        <NewBtn onClick={newSession} disabled={starting}>
          {starting ? 'Создаём…' : '+ Новый чат'}
        </NewBtn>
        {isLoading && (
          <Card>
            <Loader />
          </Card>
        )}
        {sessions.map((s) => (
          <SessionItem
            key={s.id}
            $active={s.id === activeId}
            role="button"
            tabIndex={0}
            onClick={() => setActiveId(s.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setActiveId(s.id);
              }
            }}
          >
            <SessionTitle>{s.title}</SessionTitle>
            <SessionMeta>
              <span>{fmtDate(s.updated_at)}</span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  remove(s.id);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'inherit',
                  cursor: 'pointer',
                  fontSize: 14,
                  padding: 0,
                }}
                aria-label="Удалить сессию"
                title="Удалить сессию"
              >
                ×
              </button>
            </SessionMeta>
          </SessionItem>
        ))}
        {!isLoading && sessions.length === 0 && (
          <Empty>Создайте первый чат, чтобы спросить агента про базу знаний.</Empty>
        )}
      </SidebarColumn>

      {activeId ? (
        <ChatPanel sessionId={activeId} agentCode="statut" />
      ) : (
        <Card>
          <CardSubtitle>
            Свободный чат с агентом ОСНОВА.Юрист. Задавайте вопросы по нормативной базе — агент
            подберёт релевантные пункты ГОСТов и СП.
          </CardSubtitle>
        </Card>
      )}
    </Layout>
  );
}
